/**
 * The shared shell behind every explore grid.
 *
 * Dishes, chefs and menus are the same kind of thing at the same moment in the
 * customer's journey: a set of image-forward results they can act on. They were
 * built separately and drifted into three different card designs — three heading
 * sizes for the same role, two shadow treatments, two grids. Moving between the
 * tabs therefore looked like three products, and the layout jumped on every tap.
 *
 * This module fixes that by owning the parts that must not vary:
 *
 * - one grid, so results occupy identical tracks on all three pages
 * - one media aspect ratio, so every row reserves the same box before its image
 *   loads and nothing below it moves
 * - one heading size, one weight, one shadow, one hover motion
 * - one loading, empty and list transition, so the shift between those states is
 *   identical everywhere
 *
 * A page passes its own content in. It does not restate any of this.
 */

import type { ReactNode } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { styled } from '@linaria/react'
import { MinimalEmptyState } from '@ovasabi/ui-minimal'
import { from, minimalVars as v } from '@ovasabi/ui-minimal/tokens'

/**
 * The card surface, as one CSS source.
 *
 * Three elements need it — a plain card, a link, and a button — and they must be
 * pixel-identical. Deriving them with `styled(Card)` would silently inherit the
 * div element, so a card that should be a link rendered as a `div` carrying an
 * `href`: no link role, no keyboard activation, invisible to assistive tech.
 * The surface is therefore shared as CSS and each element is declared for what it
 * actually is.
 *
 * The motion below is the single hover treatment for every explore card. It used
 * to live in a separate `exploreMotion` constant that nothing referenced, so the
 * lift was declared twice and only this copy was ever applied.
 */
const surface = `
  position: relative;
  display: flex;
  flex-direction: column;
  min-width: 0;
  overflow: hidden;
  color: inherit;
  text-decoration: none;
  background: ${v.color.bgSurface};
  border: 1px solid ${v.color.borderSubtle};
  border-radius: ${v.radius.lg};
  box-shadow: ${v.shadow.subtle};
  transition: transform 200ms ease, box-shadow 200ms ease, border-color 200ms ease;

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      transform: translateY(-3px);
      box-shadow: ${v.shadow.medium};
      border-color: ${v.color.borderStrong};
    }
  }
`

const focusRing = `
  &:focus-visible {
    outline: 3px solid ${v.color.borderFocus};
    outline-offset: 3px;
  }
`

/** The card surface. */
const Card = styled.div`
  ${surface}
`

/**
 * The clickable region of a card: the media and the title.
 *
 * A link rather than the whole card, because a card that also carries an add
 * button cannot itself be a link — a link wrapping a button is invalid and
 * swallows the click. The action slot sits outside it, so there is one link per
 * card and it is named after the thing it opens.
 *
 * Built on react-router's Link rather than a bare anchor. A raw `<a href>` is a
 * full page load, which throws away the in-app transition and leaves the app
 * state behind; only the router's Link navigates without a reload. It takes the
 * destination as `to`, not `href`: a `href` is ignored, and the link then resolves
 * to wherever the customer already is.
 */
const Link = styled(RouterLink)`
  ${focusRing}
  display: flex;
  flex-direction: column;
  min-width: 0;
  color: inherit;
  text-decoration: none;
  background: transparent;
  border: 0;
  box-shadow: none;
  cursor: pointer;
  flex: 1;
`

/** The same region as a control, for cards that act rather than navigate. */
const LinkButton = styled.button`
  ${focusRing}
  width: 100%;
  display: flex;
  flex-direction: column;
  min-width: 0;
  padding: 0;
  font: inherit;
  color: inherit;
  text-align: left;
  background: transparent;
  cursor: pointer;
  border: 0;
  box-shadow: none;
  flex: 1;
`

const Style = {
  Card,
  Link,
  LinkButton,
  /* The title's padding, hoisted out of Body so a linked title and an unlinked
     one sit at the same offset. */
  LinkBody: styled.div`
    display: flex;
    flex-direction: column;
    gap: ${v.space['2xs']};
    padding: ${v.space.md} ${v.space.md} ${v.space.xs};
    min-width: 0;
  `,
  /* One grid. The track count is the only thing a page may not change, because a
     different count is a different layout and defeats the purpose. */
  Grid: styled.div`
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    gap: ${v.space.sm};
    ${from('hand')} {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
    ${from('desk')} {
      grid-template-columns: repeat(3, minmax(0, 1fr));
    }
  `,
  /** A reserved box. Per the practices, every image reserves its box before it
     loads; an unsized image moved the content below it by its full height. */
  Media: styled.div<{ $soldOut?: boolean }>`
    position: relative;
    aspect-ratio: 4 / 3;
    overflow: hidden;
    background: ${v.color.bgSurfaceAlt};
    flex-shrink: 0;

    & > :first-child {
      width: 100%;
      height: 100%;
    }

    /* A sold-out dish is still browsable, so it recedes rather than disappears:
       the photo desaturates and the name is struck through. */
    &[data-sold-out] {
      filter: grayscale(0.5);
      opacity: 0.78;
    }
  `,
  Body: styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: ${v.space.md};
    align-content: start;
    flex: 1;
    min-width: 0;
  `,
  /* One heading size for the card's primary label. Three different sizes for this
     role was the single largest source of the layout shifting between tabs. */
  Title: styled.h3<{ $soldOut?: boolean }>`
    margin: 0;
    min-width: 0;
    font-family: ${v.typography.bodyFamily};
    font-size: ${v.typography.bodySize};
    font-weight: ${v.typography.weightBold};
    line-height: ${v.typography.lineHeightTight};
    color: ${v.color.textPrimary};
    white-space: normal;
    overflow-wrap: anywhere;
    line-height: 1.45;
    opacity: ${({ $soldOut }) => ($soldOut ? 0.7 : 1)};
    text-decoration: ${({ $soldOut }) => ($soldOut ? 'line-through' : 'none')};
  `,
  /* A div, not a p: these slots take caller content that may be a block element,
     and a <div> inside a <p> is invalid HTML that React rejects. */
  Subtitle: styled.div`
    margin: 0;
    min-width: 0;
    font-size: ${v.typography.captionSize};
    color: ${v.color.textSecondary};
    white-space: normal;
    overflow-wrap: anywhere;
    line-height: 1.45;
  `,
  Meta: styled.div`
    margin: 0;
    font-size: ${v.typography.metaSize};
    color: ${v.color.textTertiary};
  `,
  Footer: styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${v.space.sm};
    margin-top: auto;
    padding-top: ${v.space['2xs']};
    min-height: 44px;
    width: 100%;
  `,
  ActionAffordance: styled.span`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    color: ${v.color.brand};
    font-size: ${v.typography.captionSize};
    font-weight: ${v.typography.weightBold};
    letter-spacing: -0.01em;
    transition: transform 160ms ease, color 160ms ease;

    [data-explore-card]:hover & {
      transform: translateX(2px);
    }
  `,
  /* A slot for controls taller than a footer line. It keeps the shell's padding so
     a card with an action and one without share the same text baseline. */
  ActionRow: styled.div`
    display: flex;
    align-items: center;
    margin-top: auto;
    padding: 0 ${v.space.md} ${v.space.md};
    min-height: 44px;
    width: 100%;
    box-sizing: border-box;
  `,
  Action: styled.span`
    color: ${v.color.brand};
    font-size: ${v.typography.captionSize};
    font-weight: ${v.typography.weightBold};
  `,
  /* The loading surface is a fixed block of the same card shape, so swapping it
     for real results does not change the page height. */
  SkeletonCard: styled.div`
    display: flex;
    flex-direction: column;
    overflow: hidden;
    background: ${v.color.bgSurface};
    border: 1px solid ${v.color.borderSubtle};
    border-radius: ${v.radius.lg};
    box-shadow: ${v.shadow.subtle};
  `,
  SkeletonMedia: styled.div`
    aspect-ratio: 4 / 3;
    background: ${v.color.bgSurfaceAlt};
  `,
  SkeletonBody: styled.div`
    display: grid;
    gap: ${v.space.sm};
    padding: ${v.space.md};
  `,
  SkeletonLine: styled.div<{ $width: string }>`
    height: 12px;
    width: ${(p) => p.$width};
    border-radius: ${v.radius.sm};
    background: ${v.color.bgSurfaceAlt};
  `,
  /* Reserves the grid box so results appearing does not drop the page. */
  SkeletonWrap: styled.div`
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    gap: ${v.space.sm};
    ${from('hand')} {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
    ${from('desk')} {
      grid-template-columns: repeat(3, minmax(0, 1fr));
    }
    pointer-events: none;
  `,
}

export interface ExploreCardProps {
  /** Media fills the reserved box. */
  media: ReactNode
  /** The card's primary label. Rendered at the shared heading size. */
  title: string
  /** Secondary line under the title, such as a kitchen or chef. */
  subtitle?: ReactNode
  /** Tertiary line, such as a dish count. */
  meta?: ReactNode
  /** Price or action, bottom-left. */
  leading?: ReactNode
  /** Trailing affordance, bottom-right. */
  trailing?: ReactNode
  /**
   * Rendered over the media, such as a rating chip or favourite toggle. Reserved
   * by the caller, so its presence does not change the card's height.
   */
  mediaOverlay?: ReactNode
  /**
   * Replaces the footer row with richer controls, such as a quantity stepper and
   * an add button. Dishes need this and chefs do not, which is why it is a slot
   * rather than a required prop.
   */
  action?: ReactNode
  /**
   * The dish is sold out. The cover desaturates and the title is struck through.
   * The card stays browsable: a customer still needs to read what is unavailable.
   */
  soldOut?: boolean
  /**
   * Destination. Present renders the card as a link; absent with `onClick`
   * renders it as a button. An interactive card is never a div with a handler.
   */
  to?: string
  onClick?: () => void
}

/**
 * One card, used by every explore page.
 *
 * The props are the only thing that differs between a dish, a chef and a menu.
 * Structure, type scale, spacing and motion are identical by construction, which
 * is what stops the layout shifting as the customer moves between tabs.
 */
export const ExploreCard = ({
  media,
  title,
  subtitle,
  meta,
  leading,
  trailing,
  mediaOverlay,
  action,
  soldOut = false,
  to,
  onClick,
}: ExploreCardProps) => {
  const mediaBlock = (
    <Style.Media $soldOut={soldOut} data-sold-out={soldOut || undefined}>
      {media}
      {mediaOverlay}
    </Style.Media>
  )
  const titleBlock = <Style.Title $soldOut={soldOut} title={title}>{title}</Style.Title>

  // When an action slot is provided (such as stepper + Add button on dishes),
  // controls must sit outside the link to remain accessible and clickable.
  // The link wraps media, title and metadata without any inner borders.
  if (action) {
    const linkContent = (
      <>
        {mediaBlock}
        <Style.LinkBody>
          {titleBlock}
          {subtitle ? <Style.Subtitle>{subtitle}</Style.Subtitle> : null}
          {meta ? <Style.Meta>{meta}</Style.Meta> : null}
        </Style.LinkBody>
      </>
    )

    return (
      <Style.Card data-explore-card="">
        {to ? (
          <Style.Link to={to} aria-label={title}>
            {linkContent}
          </Style.Link>
        ) : onClick ? (
          <Style.LinkButton type="button" onClick={onClick} aria-label={title}>
            {linkContent}
          </Style.LinkButton>
        ) : (
          <div>{linkContent}</div>
        )}
        <Style.ActionRow>{action}</Style.ActionRow>
      </Style.Card>
    )
  }

  // When no action slot is provided (e.g. menus and chefs), the entire card
  // is interactive and navigates cleanly without any inner dividers.
  const fullContent = (
    <>
      {mediaBlock}
      <Style.Body>
        {titleBlock}
        {subtitle ? <Style.Subtitle>{subtitle}</Style.Subtitle> : null}
        {meta ? <Style.Meta>{meta}</Style.Meta> : null}
        {(leading || trailing) && (
          <Style.Footer>
            <span>{leading}</span>
            {trailing ? (
              typeof trailing === 'string' ? (
                <Style.ActionAffordance>{trailing}</Style.ActionAffordance>
              ) : (
                <span>{trailing}</span>
              )
            ) : null}
          </Style.Footer>
        )}
      </Style.Body>
    </>
  )

  return (
    <Style.Card data-explore-card="">
      {to ? (
        <Style.Link to={to} aria-label={title}>
          {fullContent}
        </Style.Link>
      ) : onClick ? (
        <Style.LinkButton type="button" onClick={onClick} aria-label={title}>
          {fullContent}
        </Style.LinkButton>
      ) : (
        fullContent
      )}
    </Style.Card>
  )
}

export interface ExploreStateProps {
  /**
   * True while the first read is in flight. Renders skeletons in the same grid
   * the results use, so the page does not jump when they land.
   */
  loading?: boolean
  /** Rendered when loading, so the reserved height matches the real list. */
  skeletonCount?: number
  children: ReactNode
}

/**
 * The loading / list transition, identical on every explore page.
 *
 * These three states were handled per page, which is why the shift differed. A
 * page passes its list and its empty copy and nothing else.
 *
 * The empty state is only ever rendered after loading finishes. Rendering it
 * during a load tells the customer a shelf is empty when it has simply not
 * answered yet, which is the most damaging version of this bug.
 */
export const ExploreState = ({
  loading = false,
  skeletonCount = 6,
  children,
}: ExploreStateProps) => {
  // Three states, not two: loading (this skeleton), empty (the page's own
  // ExploreEmpty), and list. `loading` must mean the page's read is in flight —
  // not merely that some request somewhere is — or a shelf that already has
  // content flashes placeholders over it on every background refresh.
  if (loading) {
    return (
      <Style.SkeletonWrap aria-busy="true">
        {Array.from({ length: skeletonCount }, (_, i) => (
          <Style.SkeletonCard key={i} aria-hidden="true">
            <Style.SkeletonMedia />
            <Style.SkeletonBody>
              <Style.SkeletonLine $width="70%" />
              <Style.SkeletonLine $width="45%" />
              <Style.SkeletonLine $width="30%" />
            </Style.SkeletonBody>
          </Style.SkeletonCard>
        ))}
      </Style.SkeletonWrap>
    )
  }
  // NOTE: there is deliberately no `empty` branch here. An earlier version
  // returned null for `empty`, which silently swallowed the empty state each page
  // passed as children — the shelf showed nothing at all instead of explaining
  // itself. Pages render ExploreEmpty themselves; the shell owns the loading
  // surface so the reserved height is identical.
  return <>{children}</>
}

/** The shared grid. Pages must not declare their own. */
export const ExploreGrid = ({ children }: { children: ReactNode }) => (
  <Style.Grid>{children}</Style.Grid>
)

/** The shared empty state, so all three pages read the same way. */
export const ExploreEmpty = ({
  icon,
  title,
  description,
  action,
}: {
  icon: ReactNode
  title: string
  description: string
  action?: ReactNode
}) => <MinimalEmptyState icon={icon} title={title} description={description} action={action} />

export const ExploreCardStyle = Style
